import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProfileCard from './ProfileCard';
import ProfilePreviewModal from './ProfilePreviewModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import EmptyState from './EmptyState';
import { getAllProfiles, deleteProfile, setDefaultProfile } from '../../services/profileService';
import './ProfileDashboard.css';

const ProfileDashboard = () => {
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [operationError, setOperationError] = useState(null);
    const [previewProfile, setPreviewProfile] = useState(null);
    const [deleteProfileId, setDeleteProfileId] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        loadProfiles();
    }, []);

    const loadProfiles = async () => {
        try {
            setLoading(true);
            const response = await getAllProfiles();
            setProfiles(response.data || []);
            setLoadError(null);
        } catch (err) {
            setLoadError('Failed to load profiles. Please try again.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNew = () => {
        navigate('/profile/create');
    };

    const handleEdit = (profileId) => {
        navigate(`/profile/edit/${profileId}`);
    };

    const handlePreview = (profile) => {
        setPreviewProfile(profile);
    };

    const handleDeleteClick = (profileId) => {
        setDeleteProfileId(profileId);
    };

    const handleDeleteConfirm = async () => {
        try {
            await deleteProfile(deleteProfileId);
            setDeleteProfileId(null);
            loadProfiles();
        } catch (err) {
            console.error('Delete failed:', err);
            setOperationError('Failed to delete profile. Please try again.');
            setDeleteProfileId(null);
        }
    };

    const handleSetDefault = async (profileId) => {
        try {
            await setDefaultProfile(profileId);
            loadProfiles();
        } catch (err) {
            console.error('Set default failed:', err);
            setOperationError('Failed to set default profile. Please try again.');
        }
    };

    const handleGenerateTheme = (profile) => {
        // Route to /ai-custom with profileId; add other generator types here if needed
        navigate(`/ai-custom?profileId=${profile.id}`);
    };

    if (loading) {
        return (
            <div className="profile-dashboard">
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading profiles...</p>
                </div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="profile-dashboard">
                <div className="error-state">
                    <p>{loadError}</p>
                    <button className="btn-primary" onClick={loadProfiles}>
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="profile-dashboard">
            <div className="dashboard-header">
                <div>
                    <h1>Business Profiles</h1>
                    <p>Manage your business information for theme personalization</p>
                </div>
                <button className="btn-primary" onClick={handleCreateNew}>
                    + New Profile
                </button>
            </div>

            {operationError && (
                <div className="error-state">
                    <p>{operationError}</p>
                    <button className="btn-primary" onClick={() => setOperationError(null)}>
                        Dismiss
                    </button>
                </div>
            )}

            {profiles.length === 0 ? (
                <EmptyState onCreate={handleCreateNew} />
            ) : (
                <div className="profiles-grid">
                    {profiles.map(profile => (
                        <ProfileCard
                            key={profile.id}
                            profile={profile}
                            onEdit={handleEdit}
                            onPreview={handlePreview}
                            onDelete={handleDeleteClick}
                            onSetDefault={handleSetDefault}
                            onGenerateTheme={handleGenerateTheme}
                        />
                    ))}
                </div>
            )}

            {previewProfile && (
                <ProfilePreviewModal
                    profile={previewProfile}
                    onClose={() => setPreviewProfile(null)}
                />
            )}

            {deleteProfileId && (
                <DeleteConfirmModal
                    onConfirm={handleDeleteConfirm}
                    onCancel={() => setDeleteProfileId(null)}
                />
            )}
        </div>
    );
};

export default ProfileDashboard;
